import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';

import Illustration, { IllustrationType } from '@adsk/alloy-react-illustration';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { Size, SIZES } from './types';

type EmptyStateProps = Omit<StylableComponent<HTMLDivElement>, 'title'> & {
  title?: ReactNode;
  illustrationType?: IllustrationType;
  description?: ReactNode;
  hideIllustration?: boolean;
  size?: Size;
};

const layoutBase = {
  'box-sizing': 'border-box',
  display: 'flex',
  'align-items': 'center',
  'text-align': 'center',
  'justify-content': 'center',
  'flex-direction': 'column',
} as const;

/**
 * An EmptyState
 * Empty states occur when data is not available to be displayed.
 */
const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      style,
      className,
      title,
      illustrationType = Illustration.TYPES.FOLDER_EMPTY_GREY,
      description,
      children,
      hideIllustration = false,
      size = SIZES.MEDIUM,
      ...props
    },
    ref,
  ) => {
    const currentTheme = useTheme();
    const tokens = currentTheme.tokens?.component?.emptystate;
    const sizeTokens = tokens?.[size];

    const showIllustration = Boolean(!hideIllustration && illustrationType);

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          layoutBase,
          tokens?.container,
          sizeTokens?.container,
          showIllustration && {
            '> div:first-child': tokens?.['illustration-container'],
          },
          style,
        ]}
      >
        {showIllustration && (
          <Illustration
            type={illustrationType}
            style={sizeTokens?.['illustration-container']}
          />
        )}
        {title && (
          <div dir="auto" css={sizeTokens?.headline}>
            {title}
          </div>
        )}
        {description && (
          <div dir="auto" css={sizeTokens?.description}>
            {description}
          </div>
        )}
        {children && (
          <div css={sizeTokens?.['actions-container']}>{children}</div>
        )}
      </div>
    );
  },
);

EmptyState.displayName = 'EmptyState';

EmptyState.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Empty state title (optional) */
  title: PropTypes.any,
  /** Illustration type */
  illustrationType: PropTypes.oneOf(Object.values(Illustration.TYPES)),
  /** State description */
  description: PropTypes.any,
  /** Additional state content */
  children: PropTypes.any,
  /** Whether we should hide the default illustration */
  hideIllustration: PropTypes.bool,
  /** EmptyState size */
  size: PropTypes.oneOf(Object.values(SIZES)),
};

export default Object.assign(EmptyState, {
  ILLUSTRATION_TYPES: Illustration.TYPES,
  SIZES,
});
